import classNames from 'classnames'
import { CSSProperties, useCallback, useMemo, useState } from 'react'
import { Editor, Path, Element as SlateElement, Transforms } from 'slate'
import { ReactEditor, RenderElementProps, useSlate } from 'slate-react'

import { Button } from 'core-components/library/button'
import { Menu } from 'core-components/library/menu'
import {
  AbsolutePopup,
  PopPaper,
  TPopupHorizontal,
  TPopupVertical,
  TVisibilityTrigger,
} from 'core-components/library/popup'
import { TopPanelButtonsWrapper } from 'core-components/library/richTextEditor'
import {
  updateCellBackgroundColor,
  updateCellVerticalAlign,
} from 'core-components/library/richTextEditor/helpers/slateHelper'
import {
  BLOCK_FORMATS,
  CustomEditor,
  FONT_COLORS,
  TTableElement,
} from 'core-components/library/richTextEditor/interface/custom-types'
import { TCell } from 'core-components/library/richTextEditor/interface/TCell'
import { ColorSettingsPopup } from 'core-components/library/richTextEditor/toolbar/components/colors/ColorSettingsPopup'
import { BaseCrud, Static } from 'core-components/types'

import { DATA_KEY, MIN_MOUSE_MOVE } from './constants'
import { getCol, getGridTable, TableCol } from './helpers/commonTableHelper'
import { insertColLeft, insertColRight } from './helpers/insertColumnsHelper'
import { insertRowAbowe, insertRowBelow } from './helpers/insertRowsHelper'
import { mergeCells } from './helpers/mergeCellsHelper'
import { removeCol, removeRow } from './helpers/removeHelper'
import { addSelection, removeSelection } from './helpers/selectionHelper'
import { splitCellToColumn, splitCellToRow } from './helpers/splitCellsHelper'
import { useButtonAccess } from './hooks/useButtonAccess'
import { IPrevValues } from './models/ITables'
import { TableCellsActions, TableColumnActions, TableRowActions } from './models/tableActions'

import styles from './Table.module.scss'

interface IProps {
  style: CSSProperties
  className: string
}

interface IMouseStartPosition {
  x: number
  y: number
  key: string
}

export const Table = (props: RenderElementProps & IProps) => {
  const [startCellData, setStartCellData] = useState<IMouseStartPosition | undefined>()
  const [prevSelectedPaths, setPrevSelectedPaths] = useState<IPrevValues>({
    startPath: [],
    endPath: [],
    res: [],
  })
  const editor = useSlate()
  const { enabledButtons } = useButtonAccess(editor)
  const element = useMemo(() => props.element as TTableElement, [props.element])
  const path = ReactEditor.findPath(editor, element)
  const [cellElement] = Editor.nodes(editor, {
    at: path,
    match: (n) => SlateElement.isElement(n) && n.type === BLOCK_FORMATS.tableCell,
  })

  const columnSource = useMemo(() => {
    const columnMenu = [
      { value: TableColumnActions.INSERT_LEFT },
      { value: TableColumnActions.INSERT_RIGHT },
      { value: TableColumnActions.DELETE },
    ]

    return new BaseCrud({
      fetcher: new Static(columnMenu),
      keyField: 'value',
    })
  }, [])

  const rowSource = useMemo(() => {
    const rowMenu = [
      { value: TableRowActions.INSERT_ROW_BELOW },
      { value: TableRowActions.INSERT_ROW_ABOVE },
      { value: TableRowActions.DELETE },
    ]

    return new BaseCrud({
      fetcher: new Static(rowMenu),
      keyField: 'value',
    })
  }, [])

  const splitSource = useMemo(() => {
    const splitMenu = [
      { value: TableCellsActions.SPLIT_CELL_ROW },
      { value: TableCellsActions.SPLIT_CELL_COLUMN },
    ]

    return new BaseCrud({
      fetcher: new Static(splitMenu),
      keyField: 'value',
    })
  }, [])

  const getNodeByKey = useCallback(
    (key?: string) => {
      const [node] = Editor.nodes(editor, {
        match: (node) => SlateElement.isElement(node) && (node as TCell).key === key,
        at: [],
      })

      return node
    },
    [editor]
  )

  const startNode = useMemo(() => getNodeByKey(startCellData?.key), [startCellData?.key, editor])

  const onDeleteTable = useCallback(() => {
    Transforms.removeNodes(editor, {
      at: path,
    })
    Transforms.insertNodes(
      editor,
      {
        type: BLOCK_FORMATS.paragraph,
        children: [{ text: '' }],
      },
      {
        at: path,
      }
    )
  }, [path, editor])

  const tableActionHandle = useCallback(
    (item: string) => {
      switch (item) {
        case TableColumnActions.INSERT_LEFT:
          insertColLeft(editor)
          return
        case TableColumnActions.INSERT_RIGHT:
          insertColRight(editor)
          return
        case TableColumnActions.DELETE:
          removeCol(editor)
          return
        case TableRowActions.INSERT_ROW_BELOW:
          insertRowBelow(editor)
          return
        case TableRowActions.INSERT_ROW_ABOVE:
          insertRowAbowe(editor)
          return
        case TableRowActions.DELETE:
          removeRow(editor)
          return
        case TableCellsActions.SPLIT_CELL_ROW:
          splitCellToRow(editor)
          return
        case TableCellsActions.SPLIT_CELL_COLUMN:
          splitCellToColumn(editor)
          return
        case TableCellsActions.MERGE_CELLS:
          mergeCells(editor)
          return
      }
    },
    [editor]
  )

  const isButtonDisabled = useCallback(
    (value: TableColumnActions | TableCellsActions | TableRowActions) => {
      if (value === TableColumnActions.DELETE) {
        return (
          !enabledButtons.includes(TableColumnActions.INSERT_LEFT) ||
          !enabledButtons.includes(TableColumnActions.INSERT_RIGHT)
        )
      }
      if (value === TableRowActions.DELETE) {
        return (
          !enabledButtons.includes(TableRowActions.INSERT_ROW_ABOVE) ||
          !enabledButtons.includes(TableRowActions.INSERT_ROW_BELOW)
        )
      }

      return !enabledButtons.includes(value)
    },
    [enabledButtons]
  )

  const getItemTpl = useCallback(
    (value: TableColumnActions | TableCellsActions | TableRowActions) => (
      <div className={isButtonDisabled(value) ? styles.disableField : ''}>{value}</div>
    ),
    [isButtonDisabled]
  )

  const checkStartSelect = useCallback(
    (currentX: number, currentY: number) => {
      if (startCellData) {
        const isMovedX = Math.abs(currentX - startCellData.x) >= MIN_MOUSE_MOVE
        const isMovedY = Math.abs(currentY - startCellData.y) >= MIN_MOUSE_MOVE

        return isMovedX || isMovedY
      }

      return false
    },
    [startCellData]
  )

  const updateCell = useCallback(
    <T,>(
      updatableValue: T,
      updateFunction: (editor: CustomEditor, path: Path, updatableValue: T) => void
    ) => {
      const node = Editor.above(editor, {
        match: (n) => SlateElement.isElement(n) && n.type === BLOCK_FORMATS.tableCell,
      })
      if (node) {
        updateFunction(editor, node[1], updatableValue)
      }
    },
    [editor]
  )

  const changePrevSelectedPaths = useCallback((value: IPrevValues) => {
    setPrevSelectedPaths(value)
  }, [])

  const layoutPopupContent = useMemo(
    () => (
      <PopPaper className={styles.popup}>
        <div className={styles.popup_content}>
          <Button variant={'text'} onClick={() => updateCell('top', updateCellVerticalAlign)}>
            Top alignment
          </Button>
          <Button variant={'text'} onClick={() => updateCell('bottom', updateCellVerticalAlign)}>
            Bottom alignment
          </Button>
          <Button variant={'text'} onClick={() => updateCell('middle', updateCellVerticalAlign)}>
            Center alignment
          </Button>
        </div>
      </PopPaper>
    ),
    [updateCell]
  )

  const tablePopupContent = useMemo(
    () => (
      <PopPaper className={styles.popup}>
        <div className={styles.popup_content}>
          <Menu
            content={() => <Button variant={'text'}>Column</Button>}
            onChoose={(item) => {
              tableActionHandle(item)
            }}
            itemTpl={(item) => getItemTpl(item.value)}
            source={columnSource}
            caption={'value'}
          />
          <Menu
            content={() => <Button variant={'text'}>Row</Button>}
            onChoose={(item) => {
              tableActionHandle(item)
            }}
            itemTpl={(item) => getItemTpl(item.value)}
            source={rowSource}
            caption={'value'}
          />
          <Menu
            content={() => <Button variant={'text'}>Split</Button>}
            onChoose={(item) => {
              tableActionHandle(item)
            }}
            itemTpl={(item) => getItemTpl(item.value)}
            source={splitSource}
            caption={'value'}
          />
          <Button
            disabled={isButtonDisabled(TableCellsActions.MERGE_CELLS)}
            variant={'text'}
            onClick={() => tableActionHandle(TableCellsActions.MERGE_CELLS)}
          >
            Merge
          </Button>
          <AbsolutePopup
            horizontalStartPosition={TPopupHorizontal.Left}
            verticalStartPosition={TPopupVertical.Bottom}
            popupContent={() => {
              return (
                <PopPaper>
                  <ColorSettingsPopup
                    onClick={(color) => {
                      if (color) {
                        const { gridTable } = getGridTable(editor)
                        const selectedCols = getCol(
                          gridTable,
                          (n: TableCol) => n.cell.selectedCell === true
                        )
                        const originPaths = selectedCols.map((col) => col.originPath)
                        updateCellBackgroundColor(editor, originPaths, color)
                        removeSelection(editor, changePrevSelectedPaths)
                      }
                    }}
                    colors={FONT_COLORS}
                    isBackgroundColor={true}
                  />
                </PopPaper>
              )
            }}
          >
            <Button variant={'text'}>Background color</Button>
          </AbsolutePopup>
        </div>
      </PopPaper>
    ),
    [columnSource, rowSource, splitSource, getItemTpl, isButtonDisabled, tableActionHandle]
  )

  if (editor.disabled) {
    return (
      <div className={styles.tableScroller}>
        <table
          style={props.style}
          className={classNames(styles.table, props.className || '', {
            [styles.layoutTable]: props.element['isLayout'],
          })}
        >
          <tbody {...props.attributes}>{props.children}</tbody>
        </table>
      </div>
    )
  }
  return (
    <AbsolutePopup
      verticalDirection={TPopupVertical.Top}
      horizontalDirection={TPopupHorizontal.Right}
      visibilityTrigger={TVisibilityTrigger.Hover}
      popupContent={() => (props.element['isLayout'] ? layoutPopupContent : tablePopupContent)}
    >
      <TopPanelButtonsWrapper onDelete={editor.disabled ? undefined : onDeleteTable}>
        <div className={styles.tableScroller}>
          <table
            style={{
              ...props.style,
              ...{ borderColor: (cellElement?.[0] as TCell)?.borderColor },
            }}
            className={classNames(props.className, styles.table)}
            onDragStart={(e) => e.preventDefault()}
            onMouseDown={(e) => {
              const cell = (e.target as HTMLBaseElement).closest('td')
              const key = cell?.getAttribute(DATA_KEY) || ''
              setStartCellData({ x: e.clientX, y: e.clientY, key: key })
              removeSelection(editor, changePrevSelectedPaths)
            }}
            onMouseMove={(e) => {
              const cell = (e.target as HTMLBaseElement).closest('td')
              if (startCellData?.key) {
                const isStartSelect = checkStartSelect(e.clientX, e.clientY)
                if (cell && isStartSelect) {
                  const endKey = cell.getAttribute(DATA_KEY) || ''
                  const endNode = getNodeByKey(endKey)

                  addSelection({
                    editor,
                    startPath: Editor.path(editor, startNode[1]),
                    endPath: Editor.path(editor, endNode[1]),
                    prevSelectedPaths,
                    changePrevSelectedPaths,
                  })
                }
              }
            }}
            onMouseUp={() => {
              setStartCellData(undefined)
            }}
          >
            <tbody {...props.attributes}>{props.children}</tbody>
          </table>
        </div>
      </TopPanelButtonsWrapper>
    </AbsolutePopup>
  )
}
